/**
 * The two row shapes the autonomy card is built from, so the ladder and the
 * tune panel speak one language:
 *
 *   DisclosureRow   mark · name – summary · trailing · chevron
 *                   The mark is a LevelCheck; the rest of the row is an
 *                   accordion trigger that opens `children`.
 *   LeafRow         mark · label · trailing
 *                   The whole row is the LevelCheck's click target.
 *
 * A mark and a button cannot nest (HTML forbids interactive content inside a
 * button), which is why the mark sits BESIDE the trigger, in a 24px hit
 * target aligned to the row's first line.
 */

import type { ReactNode } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import type { LevelCheckState } from '@/lib/base/forms/level-check';
import { LevelCheck } from '@/lib/base/forms/level-check';
import { cx } from '@/utils/cx';

/** One motion curve for every state change on a row. */
export const ROW_MOTION = 'duration-200 ease-out motion-reduce:transition-none';

/** Indent for content beneath a row: mark column (16px) + gap (12px). */
export const ROW_INDENT = 'pl-7';

const TRIGGER_CLASSES =
  '-mr-2 -ml-1 flex min-w-0 flex-1 items-start gap-3 rounded-md py-1.5 pr-2 pl-1 text-left transition-colors hover:bg-secondary';

interface RowTextProps {
  title: string;
  summary?: string;
  dim: boolean;
}

/**
 * The disclosure chevron, INLINE after the row's last word rather than
 * pinned to the row's far edge. Pinned, it sat hundreds of pixels from the
 * text it belonged to and the rows did not read as expandable; inline, the
 * row reads "Deals – Follows Most updates ›" wherever the text wraps.
 */
export function RowChevron() {
  return (
    <AccordionChevron
      variant='swap'
      className='ml-1 inline-block size-4 align-[-3px] text-fg-tertiary'
    />
  );
}

/** "Name – summary ›", wrapping rather than truncating: the summary IS the
 *  row's content. */
function RowText({ title, summary, dim }: RowTextProps) {
  return (
    <span className='min-w-0 flex-1 text-sm leading-5'>
      <span
        className={cx(
          'font-medium transition-colors',
          ROW_MOTION,
          dim ? 'text-fg-tertiary' : 'text-fg-primary'
        )}
      >
        {title}
      </span>
      {summary && (
        <span className='text-fg-tertiary'>
          {' – '}
          {summary}
        </span>
      )}
      <RowChevron />
    </span>
  );
}

export interface DisclosureRowProps {
  mark: LevelCheckState;
  /** The state the mark would take under a preview elsewhere (ghosted). */
  markPreview?: LevelCheckState | null;
  /** Accessible name of the mark (the row text belongs to the trigger). */
  markLabel: string;
  markReadOnly?: boolean;
  isDisabled?: boolean;
  onMark(next: boolean): void;
  title: string;
  summary?: string;
  /** Counts, hints, badges — right-aligned annotations. */
  trailing?: ReactNode;
  /** The expanded content. */
  children: ReactNode;
}

export function DisclosureRow({
  mark,
  markPreview = null,
  markLabel,
  markReadOnly = false,
  isDisabled = false,
  onMark,
  title,
  summary,
  trailing,
  children,
}: DisclosureRowProps) {
  return (
    <AccordionItem>
      <div className='flex items-start gap-3'>
        {/* Row is 32px (py-1.5 + one 20px line); the 24px hit target sits
            4px down to centre the mark on the first line. */}
        <LevelCheck
          className='mt-1'
          state={mark}
          preview={markPreview}
          aria-label={markLabel}
          isReadOnly={markReadOnly}
          isDisabled={isDisabled}
          onChange={onMark}
        />
        <AccordionTrigger className={TRIGGER_CLASSES}>
          <RowText title={title} summary={summary} dim={mark === 'unchecked'} />
          {trailing}
        </AccordionTrigger>
      </div>
      <AccordionPanel className={cx('pb-1', ROW_INDENT)}>
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}

export interface LeafRowProps {
  mark: LevelCheckState;
  /** The state the mark would take under a preview elsewhere (ghosted). */
  markPreview?: LevelCheckState | null;
  /** Overrides the visible label as the accessible name — use it to
   *  disambiguate the same verb across scopes ("Merge duplicates – Deals"). */
  markLabel?: string;
  markReadOnly?: boolean;
  isDisabled?: boolean;
  onMark(next: boolean): void;
  label: string;
  trailing?: ReactNode;
}

export function LeafRow({
  mark,
  markPreview = null,
  markLabel,
  markReadOnly = false,
  isDisabled = false,
  onMark,
  label,
  trailing,
}: LeafRowProps) {
  const on = mark !== 'unchecked';
  const interactive = !isDisabled && !markReadOnly;
  return (
    <LevelCheck
      size='sm'
      className={cx(
        '-mx-1 w-[calc(100%+0.5rem)] rounded-md px-1 py-1 transition-colors',
        interactive && 'hover:bg-secondary'
      )}
      state={mark}
      preview={markPreview}
      aria-label={markLabel}
      isReadOnly={markReadOnly}
      isDisabled={isDisabled}
      onChange={onMark}
    >
      <span
        className={cx(
          'min-w-0 flex-1 text-sm transition-colors',
          ROW_MOTION,
          on ? 'text-fg-secondary' : 'text-fg-tertiary'
        )}
      >
        {label}
      </span>
      {trailing}
    </LevelCheck>
  );
}
